import { c as _c } from "react/compiler-runtime";
import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { Atom, Brain, ChevronDown, Sparkles, Zap } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItemRight, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCodesmithSettingsForOrg } from "@/hooks/useCodesmithSettings";
import { useOrgFromPathname } from "@/hooks/useOrgFromPathname";
import { ShortcutKeys } from "@/components/ui/shortcut-keys";
import { safeLocalStorage } from "@/lib/storage";
import { cn } from "@/lib/utils";
import { OP_MODEL_MODES, type OpModelMode } from "../codesmith/thread/opLog";
import { useTickerTransition } from "./useTickerTransition";
const MODEL_MODE_STORAGE_KEY = "blacksmith_codesmith_model_mode";
function getStoredModelMode(): OpModelMode {
  const storedMode = safeLocalStorage.get(MODEL_MODE_STORAGE_KEY);
  return OP_MODEL_MODES.includes(storedMode as OpModelMode) ? storedMode as OpModelMode : "fast";
}

// Mode grammar: `<family>[_zdr[_opt_out]]`, plus the standalone matrix mode
// `fast_grok`. These parsers are the only places that decode the grammar;
// everything else derives from them. The fusion modes stand outside the
// grammar — they name a lead/worker pairing, not a family and retention
// arm — so they are matched by name.
function familyOf(mode: OpModelMode): "fast" | "deep" {
  return mode.startsWith("fast") ? "fast" : "deep";
}
function retentionOf(mode: OpModelMode): "" | "_zdr" | "_zdr_opt_out" {
  if (mode.endsWith("_zdr_opt_out")) return "_zdr_opt_out";
  return mode.endsWith("_zdr") ? "_zdr" : "";
}
function isFusion(mode: OpModelMode): boolean {
  return mode.startsWith("fusion");
}

/** Backend-resolved preset metadata for one matrix mode's tooltip. */
export type MatrixModelInfo = {
  name: string;
  /** Agent slot (primary/sidekick/explore/general/compaction/oracle) -> provider-qualified model id, e.g. `xai/grok-4.6`. */
  models?: Record<string, string>;
};

/**
 * Gates for the composer's model switcher: the per-installation
 * `codesmith_model_switcher_enabled` (Fast/Deep),
 * `codesmith_fusion_enabled` (Fusion/Fusion Plus), and
 * `codesmith_model_matrix_enabled` (internal testing matrix) flags, all
 * flipped via feature-flag tooling with no settings UI. Kept out of
 * `AgentChatInput` so the composer itself stays free of data hooks;
 * callers resolve the gates and pass them down as props.
 */
export function useModelSwitcherSettings() {
  const $ = _c(8);
  const orgFromUrl = useOrgFromPathname();
  const {
    data
  } = useCodesmithSettingsForOrg(orgFromUrl);
  const t0 = (data?.codesmith_model_switcher_enabled ?? false) || (data?.codesmith_model_matrix_enabled ?? false);
  const t1 = data?.codesmith_fusion_enabled ?? false;
  const t2 = data?.codesmith_model_matrix_enabled ?? false;
  let t3;
  if ($[0] !== data?.codesmith_model_matrix_models) {
    t3 = data?.codesmith_model_matrix_models ?? {};
    $[0] = data?.codesmith_model_matrix_models;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const t4 = data?.codesmith_zdr_opted_out ?? false;
  let t5;
  if ($[2] !== t0 || $[3] !== t1 || $[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      enabled: t0,
      fusionEnabled: t1,
      matrixEnabled: t2,
      matrixModels: t3,
      zdrOptedOut: t4
    };
    $[2] = t0;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
}

/**
 * Maps a saved mode into the set the active switcher offers, preserving
 * the fast/deep half (e.g. "fast" → "fast_zdr" when the matrix is on,
 * "fast_zdr_opt_out" → "fast" when it is off). Fusion modes survive as-is
 * wherever the fusion flag is on; gated ones fall back to their Deep arm.
 */
function coerceModeForSwitcher(mode: OpModelMode, matrixEnabled: boolean, fusionEnabled: boolean): OpModelMode {
  if (isFusion(mode) && fusionEnabled) return mode;
  if (!matrixEnabled) {
    return familyOf(mode);
  }
  if (mode === "fast_grok") return mode;
  return `${familyOf(mode)}${retentionOf(mode) || "_zdr"}` as OpModelMode;
}
type ModeOption = {
  mode: OpModelMode;
  label: string;
  description: string;
  icon: typeof Zap;
};
const MODE_OPTIONS: ModeOption[] = [{
  mode: "fast",
  label: "Fast",
  description: "Quicker replies",
  icon: Zap
}, {
  mode: "deep",
  label: "Deep",
  description: "Most thorough",
  icon: Brain
}, {
  mode: "fusion",
  label: "Fusion",
  description: "Lead + Luna worker",
  icon: Atom
}, {
  mode: "fusion_grok",
  label: "Fusion Plus",
  description: "Lead + Grok 4.6 worker",
  icon: Sparkles
}];
const NON_FUSION_MODE_OPTIONS = MODE_OPTIONS.filter(option => !isFusion(option.mode));
const FUSION_MODE_OPTIONS = MODE_OPTIONS.filter(option => isFusion(option.mode));
function customerModeOptions(fusionEnabled: boolean): ModeOption[] {
  return fusionEnabled ? MODE_OPTIONS : NON_FUSION_MODE_OPTIONS;
}

/**
 * ZDR installations (the default) dispatch the same Sol lead + Luna worker
 * pairing for both Fusion choices, since Fable's provider requires data
 * retention (see CodesmithService's fusion preset map), so their tooltips
 * must not promise the retention-only pairings.
 */
function withZdrFusionDescriptions(options: ModeOption[]): ModeOption[] {
  return options.map(option => isFusion(option.mode) ? {
    ...option,
    description: "Lead + Luna worker"
  } : option);
}

/**
 * Persisted model selection, sticky across sessions via localStorage.
 * `onModeChange` reports explicit user selections so a caller above a
 * remount boundary can re-seed `initialMode` with the user's choice.
 */
export function useModelMode(initialMode, onModeChange, t0, t1) {
  const $ = _c(17);
  const matrixEnabled = t0 === undefined ? false : t0;
  const fusionEnabled = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== initialMode) {
    t2 = () => initialMode ?? getStoredModelMode();
    $[0] = initialMode;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [selectedMode, setSelectedMode] = useState(t2);
  let t3;
  if ($[2] !== fusionEnabled || $[3] !== matrixEnabled || $[4] !== selectedMode) {
    t3 = coerceModeForSwitcher(selectedMode, matrixEnabled, fusionEnabled);
    $[2] = fusionEnabled;
    $[3] = matrixEnabled;
    $[4] = selectedMode;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const modelMode = t3;
  let t4;
  if ($[6] !== onModeChange) {
    t4 = mode => {
      setSelectedMode(mode);
      safeLocalStorage.set(MODEL_MODE_STORAGE_KEY, mode);
      onModeChange?.(mode);
    };
    $[6] = onModeChange;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const setModelMode = t4;
  let t5;
  if ($[8] !== fusionEnabled || $[9] !== matrixEnabled || $[10] !== modelMode || $[11] !== setModelMode) {
    t5 = () => {
      if (matrixEnabled) {
        const flippedFamily = familyOf(modelMode) === "fast" ? "deep" : "fast";
        setModelMode(coerceModeForSwitcher(`${flippedFamily}${retentionOf(modelMode)}` as OpModelMode, matrixEnabled, fusionEnabled));
        return;
      }
      const options = customerModeOptions(fusionEnabled);
      const index = options.findIndex(option => option.mode === modelMode);
      setModelMode(options[(index + 1) % options.length].mode);
    };
    $[8] = fusionEnabled;
    $[9] = matrixEnabled;
    $[10] = modelMode;
    $[11] = setModelMode;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const toggleModelMode = t5;
  let t6;
  if ($[13] !== modelMode || $[14] !== setModelMode || $[15] !== toggleModelMode) {
    t6 = {
      modelMode,
      setModelMode,
      toggleModelMode
    };
    $[13] = modelMode;
    $[14] = setModelMode;
    $[15] = toggleModelMode;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  return t6;
}

// Matrix descriptions are fallbacks only: the rendered tooltip prefers the
// backend-resolved label in `matrixModels` (settings payload), which follows
// the live rollout presets per environment.
const MODEL_MATRIX_OPTIONS: ModeOption[] = [{
  mode: "fast_zdr",
  label: "Fast (ZDR)",
  description: "GPT 5.6 Sol",
  icon: Zap
}, {
  mode: "fast_zdr_opt_out",
  label: "Fast",
  description: "GPT 5.6 Sol",
  icon: Zap
}, {
  mode: "fast_grok",
  label: "Fast (Grok)",
  description: "Grok 4.6",
  icon: Zap
}, {
  mode: "deep_zdr",
  label: "Deep (ZDR)",
  description: "GPT 5.6 Sol (extra high)",
  icon: Brain
}, {
  mode: "deep_zdr_opt_out",
  label: "Deep",
  description: "Claude Fable 5.1",
  icon: Brain
}];
const AGENT_SLOTS = new Set(["primary", "sidekick"]);

/** `anthropic-data-retention/claude-fable-5-1 (high)` → `claude-fable-5-1 (high)` */
function shortModelName(model: string): string {
  return model.slice(model.indexOf("/") + 1);
}
function ModelSlotRows(t0) {
  const $ = _c(9);
  const {
    heading,
    entries
  } = t0;
  let t1;
  if ($[0] !== heading) {
    t1 = heading && <div className="type-body-xxs-medium uppercase tracking-wide text-neutral-400">{heading}</div>;
    $[0] = heading;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== entries) {
    t2 = entries.map(_temp);
    $[2] = entries;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = <dl className="grid grid-cols-[auto_1fr] gap-x-2">{t2}</dl>;
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t3) {
    t4 = <div>{t1}{t3}</div>;
    $[6] = t1;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function _temp(t0) {
  const [slot, model] = t0;
  return <div key={slot} className="contents"><dt className="capitalize text-neutral-400">{slot}</dt><dd className="font-mono">{shortModelName(model)}</dd></div>;
}
function MatrixModelTooltip(t0) {
  const $ = _c(20);
  const {
    info,
    fallback
  } = t0;
  if (!info) {
    let t1;
    if ($[0] !== fallback) {
      t1 = <>{fallback}</>;
      $[0] = fallback;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let agents;
  let entries;
  let t1;
  let t2;
  if ($[2] !== info.models || $[3] !== info.name) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      entries = Object.entries(info.models ?? {});
      if (entries.length === 0) {
        let t3;
        if ($[8] !== info.name) {
          t3 = <>{info.name}</>;
          $[8] = info.name;
          $[9] = t3;
        } else {
          t3 = $[9];
        }
        t2 = t3;
        break bb0;
      }
      agents = entries.filter(_temp2);
      t1 = entries.filter(_temp3);
    }
    $[2] = info.models;
    $[3] = info.name;
    $[4] = agents;
    $[5] = entries;
    $[6] = t1;
    $[7] = t2;
  } else {
    agents = $[4];
    entries = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const subagents = t1;
  const grouped = agents.length > 0 && subagents.length > 0;
  let t3;
  if ($[10] !== info.name) {
    t3 = <div className="font-medium">{info.name}</div>;
    $[10] = info.name;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== agents || $[13] !== entries || $[14] !== grouped || $[15] !== subagents) {
    t4 = grouped ? <><ModelSlotRows heading="Agents" entries={agents} /><ModelSlotRows heading="Subagents" entries={subagents} /></> : <ModelSlotRows entries={entries} />;
    $[12] = agents;
    $[13] = entries;
    $[14] = grouped;
    $[15] = subagents;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== t3 || $[18] !== t4) {
    t5 = <div className="space-y-1.5">{t3}{t4}</div>;
    $[17] = t3;
    $[18] = t4;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  return t5;
}
function _temp3(t0) {
  const [slot_0] = t0;
  return !AGENT_SLOTS.has(slot_0);
}
function _temp2(t0) {
  const [slot] = t0;
  return AGENT_SLOTS.has(slot);
}
function ModelModeTicker(t0) {
  const $ = _c(44);
  const {
    mode,
    options
  } = t0;
  const transition = useTickerTransition(mode);
  const currentMode = transition.current;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = new Map();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const sizerRefs = useRef(t1);
  const [width, setWidth] = useState();
  let t2;
  if ($[1] !== currentMode) {
    t2 = () => {
      const sizer = sizerRefs.current.get(currentMode);
      if (!sizer) {
        return;
      }
      const measure = () => setWidth(sizer.getBoundingClientRect().width);
      measure();
      const observer = new ResizeObserver(measure);
      observer.observe(sizer);
      return () => observer.disconnect();
    };
    $[1] = currentMode;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== currentMode || $[4] !== options) {
    t3 = [currentMode, options];
    $[3] = currentMode;
    $[4] = options;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  useLayoutEffect(t2, t3);
  let t4;
  if ($[6] !== currentMode || $[7] !== options) {
    let t5;
    if ($[9] !== currentMode) {
      t5 = option => option.mode === currentMode;
      $[9] = currentMode;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    t4 = options.find(t5);
    $[6] = currentMode;
    $[7] = options;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const current = t4;
  let CurrentIcon;
  let currentLabel;
  let t10;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[11] !== current?.icon || $[12] !== current?.label || $[13] !== options || $[14] !== transition.current || $[15] !== transition.previous || $[16] !== width) {
    let t11;
    if ($[25] !== transition.previous) {
      t11 = option_0 => option_0.mode === transition.previous;
      $[25] = transition.previous;
      $[26] = t11;
    } else {
      t11 = $[26];
    }
    const previous = options.find(t11);
    CurrentIcon = current?.icon ?? Brain;
    const PreviousIcon = previous?.icon;
    currentLabel = current?.label ?? "Deep";
    const previousLabel = previous?.label;
    t7 = "relative flex items-center justify-end overflow-x-clip transition-[width] duration-[240ms] ease-in-out motion-reduce:transition-none";
    if ($[27] !== width) {
      t8 = width === undefined ? undefined : {
        width
      };
      $[27] = width;
      $[28] = t8;
    } else {
      t8 = $[28];
    }
    if ($[29] !== options) {
      t9 = options.map(option_1 => <span key={`sizer-${option_1.mode}`} ref={el => {
        if (el) {
          sizerRefs.current.set(option_1.mode, el);
        } else {
          sizerRefs.current.delete(option_1.mode);
        }
      }} className="invisible absolute left-0 flex items-center gap-1 whitespace-nowrap after:content-[attr(data-label)]" data-label={option_1.label} aria-hidden={true}><option_1.icon className="size-3 shrink-0" strokeWidth={2} /></span>);
      $[29] = options;
      $[30] = t9;
    } else {
      t9 = $[30];
    }
    t10 = previous && PreviousIcon && <span key={previous.mode} className="label-ticker-exit absolute inset-y-0 right-0 flex items-center gap-1 whitespace-nowrap" aria-hidden={true}><PreviousIcon className="size-3" strokeWidth={2} />{previousLabel}</span>;
    t5 = transition.current;
    t6 = cn("flex items-center gap-1 whitespace-nowrap", previous && "label-ticker-enter");
    $[11] = current?.icon;
    $[12] = current?.label;
    $[13] = options;
    $[14] = transition.current;
    $[15] = transition.previous;
    $[16] = width;
    $[17] = CurrentIcon;
    $[18] = currentLabel;
    $[19] = t10;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    CurrentIcon = $[17];
    currentLabel = $[18];
    t10 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t11;
  if ($[31] !== CurrentIcon) {
    t11 = <CurrentIcon className="size-3" strokeWidth={2} aria-hidden={true} />;
    $[31] = CurrentIcon;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== currentLabel || $[34] !== t11 || $[35] !== t5 || $[36] !== t6) {
    t12 = <span key={t5} className={t6}>{t11}{currentLabel}</span>;
    $[33] = currentLabel;
    $[34] = t11;
    $[35] = t5;
    $[36] = t6;
    $[37] = t12;
  } else {
    t12 = $[37];
  }
  let t13;
  if ($[38] !== t10 || $[39] !== t12 || $[40] !== t7 || $[41] !== t8 || $[42] !== t9) {
    t13 = <span className={t7} style={t8}>{t9}{t10}{t12}</span>;
    $[38] = t10;
    $[39] = t12;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
    $[43] = t13;
  } else {
    t13 = $[43];
  }
  return t13;
}
export function ModelModeSwitcher(t0) {
  const $ = _c(42);
  const {
    mode,
    onModeChange,
    disabled,
    shortcutKeys: t1,
    matrixEnabled: t2,
    fusionEnabled: t3,
    matrixModels,
    zdrOptedOut: t4
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? ["\u2318", "\u21E7", "."] : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const shortcutKeys = t5;
  const matrixEnabled = t2 === undefined ? false : t2;
  const fusionEnabled = t3 === undefined ? false : t3;
  const zdrOptedOut = t4 === undefined ? false : t4;
  let t6;
  if ($[2] !== fusionEnabled || $[3] !== matrixEnabled || $[4] !== zdrOptedOut) {
    const baseOptions = matrixEnabled ? fusionEnabled ? [...MODEL_MATRIX_OPTIONS, ...FUSION_MODE_OPTIONS] : MODEL_MATRIX_OPTIONS : customerModeOptions(fusionEnabled);
    t6 = zdrOptedOut ? baseOptions : withZdrFusionDescriptions(baseOptions);
    $[2] = fusionEnabled;
    $[3] = matrixEnabled;
    $[4] = zdrOptedOut;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const options = t6;
  let t7;
  if ($[6] !== fusionEnabled || $[7] !== matrixEnabled || $[8] !== mode) {
    t7 = coerceModeForSwitcher(mode, matrixEnabled, fusionEnabled);
    $[6] = fusionEnabled;
    $[7] = matrixEnabled;
    $[8] = mode;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const selectedMode = t7;
  let t8;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = cn("flex h-8 cursor-pointer items-center gap-1 rounded-full border border-transparent px-1.5 text-xs text-icon-secondary duration-0 delay-0 hover:text-neutral-700 focus-visible:border-2 focus-visible:border-[var(--focus-border)] focus-visible:outline-none focus-visible:ring-0 disabled:opacity-50 dark:hover:text-neutral-200", "data-[state=open]:focus-visible:border-transparent data-[state=open]:dark:focus-visible:border-transparent");
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== options || $[12] !== selectedMode) {
    t9 = <ModelModeTicker mode={selectedMode} options={options} />;
    $[11] = options;
    $[12] = selectedMode;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <ChevronDown className="size-3" strokeWidth={2} aria-hidden={true} />;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== disabled || $[16] !== t9) {
    t11 = <TooltipTrigger asChild={true}><span className="inline-flex"><DropdownMenuTrigger asChild={true}><button type="button" disabled={disabled} aria-label="Select model mode" className={t8}>{t9}{t10}</button></DropdownMenuTrigger></span></TooltipTrigger>;
    $[15] = disabled;
    $[16] = t9;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  const t12 = matrixEnabled ? "Select model and retention" : "Switch model mode";
  let t13;
  if ($[18] !== t12) {
    t13 = <span>{t12}</span>;
    $[18] = t12;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  let t14;
  if ($[20] !== shortcutKeys) {
    t14 = <ShortcutKeys keys={shortcutKeys} />;
    $[20] = shortcutKeys;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] !== t13 || $[23] !== t14) {
    t15 = <TooltipContent className="flex items-center gap-2 px-2 py-1 text-xs">{t13}{t14}</TooltipContent>;
    $[22] = t13;
    $[23] = t14;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t11 || $[26] !== t15) {
    t16 = <TooltipProvider delayDuration={200}><Tooltip>{t11}{t15}</Tooltip></TooltipProvider>;
    $[25] = t11;
    $[26] = t15;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  let t17;
  if ($[28] !== onModeChange) {
    t17 = value => onModeChange(value as OpModelMode);
    $[28] = onModeChange;
    $[29] = t17;
  } else {
    t17 = $[29];
  }
  let t18;
  if ($[30] !== matrixModels || $[31] !== options) {
    let t19;
    if ($[33] !== matrixModels) {
      t19 = option => <Tooltip key={option.mode}><TooltipTrigger asChild={true}><DropdownMenuRadioItemRight value={option.mode} className="justify-start px-2 delay-0 duration-0"><span className="flex items-center gap-2"><option.icon className="size-3.5 shrink-0 text-icon-secondary" strokeWidth={2} aria-hidden={true} /><span>{option.label}</span></span></DropdownMenuRadioItemRight></TooltipTrigger><TooltipContent side="right" sideOffset={8} className="text-xs"><MatrixModelTooltip info={matrixModels?.[option.mode]} fallback={option.description} /></TooltipContent></Tooltip>;
      $[33] = matrixModels;
      $[34] = t19;
    } else {
      t19 = $[34];
    }
    t18 = options.map(t19);
    $[30] = matrixModels;
    $[31] = options;
    $[32] = t18;
  } else {
    t18 = $[32];
  }
  let t19;
  if ($[35] !== selectedMode || $[36] !== t17 || $[37] !== t18) {
    t19 = <DropdownMenuContent align="start" side="bottom" sideOffset={8}><TooltipProvider delayDuration={300}><DropdownMenuRadioGroup value={selectedMode} onValueChange={t17}>{t18}</DropdownMenuRadioGroup></TooltipProvider></DropdownMenuContent>;
    $[35] = selectedMode;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t16 || $[40] !== t19) {
    t20 = <DropdownMenu modal={false}>{t16}{t19}</DropdownMenu>;
    $[39] = t16;
    $[40] = t19;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  return t20;
}